import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Icon, MerchantIcon } from './icons';
import { money } from './format';
import { buildCategoryList, getCategoryMeta, CATEGORY_ICON_LIBRARY, CATEGORY_TINTS } from './categoryMeta';
import { isMoneyIn } from '../lib/txType';

const TYPE_LABELS = {
  income: 'Income',
  expense: 'Expense',
  refund: 'Refund',
  repayment: 'Card payment',
};

// The one sheet shell. Breakpoints, radii, scrim and field metrics all live in
// App.css (.sheet-*, .field) so an editor cannot quietly invent its own — which
// is how the app ended up with 22px, 24px, 13px and 14px corners, and buttons
// at 38, 42, 44, 48 and 50px, across five files.
function Modal({ open, onClose, children, title, width }) {
  useEffect(() => {
    const fn = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (open) document.addEventListener('keydown', fn);
    return () => document.removeEventListener('keydown', fn);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="sheet-scrim" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="sheet scroll"
        role="dialog"
        aria-modal="true"
        aria-label={title || undefined}
        style={width ? { maxWidth: width } : undefined}
      >
        <div className="sheet-grab" />
        {/* The close button is unconditional. It used to live inside the
            `title &&` block, which made a visible way out a side effect of
            having a heading — so the one sheet opened without a title (the
            transaction detail, below) offered no way to dismiss it but
            guessing that the backdrop is clickable. On mobile the drag handle
            hints at it; on desktop there was nothing at all. The title is the
            optional half. */}
        <div className="sheet-head">
          {title ? <h2 className="sheet-title">{title}</h2> : <span />}
          <button type="button" onClick={onClose} aria-label="Close" className="sheet-close pressable">
            <Icon name="plus" size={17} style={{ transform: 'rotate(45deg)' }} />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body
  );
}

export function TxDetail({ tx, accountName, onClose, onEdit, onDelete }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  if (!tx) return null;
  const display = tx.raw || tx;
  const d = display.date instanceof Date ? display.date : new Date(display.date);
  const rows = [
    ['Category', display.cat || display.category],
    ['Date', d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })],
    ['Type', TYPE_LABELS[display.type] || 'Expense'],
    ['Recurring', display.recurring || display.isRecurring ? `Yes · ${display.recurrenceType || 'monthly'}` : 'No'],
    ...(accountName ? [['Account', accountName]] : []),
  ];
  const amt = display.amt ?? display.amount;
  const type = display.type;

  return (
    <Modal open={!!tx} onClose={onClose}>
      {/* Left-aligned, like every other figure in the app. Centred, this was
          the one place a merchant name and an amount sat on the page's axis
          instead of its margin, and it read as a receipt rather than a row. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
        <MerchantIcon logoUrl={display.logoUrl || tx.logoUrl} website={display.website || tx.website} cat={display.cat || display.category} size={44} radius={12} />
        <div style={{ minWidth: 0 }}>
          <div className="title" style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)' }}>
            {tx.desc || display.desc || display.description}
          </div>
          <div
            className="num"
            style={{
              fontSize: 28, fontWeight: 600, letterSpacing: '-0.03em', marginTop: 2,
              color: isMoneyIn(type) ? 'var(--pos)' : 'var(--ink)',
            }}
          >
            {money(isMoneyIn(type) ? amt : -amt, { sign: true })}
          </div>
        </div>
      </div>
      {/* Hairline rows, not a card inside a sheet — one level of grouping. */}
      <div style={{ marginBottom: 20 }}>
        {rows.map((r) => (
          <div key={r[0]} className="row-line" style={{ padding: '11px 0', margin: 0, width: '100%' }}>
            <span style={{ flex: 1, fontSize: 13.5, color: 'var(--ink-3)' }}>{r[0]}</span>
            <span style={{ fontSize: 13.5, color: 'var(--ink)', fontWeight: 500 }}>{r[1]}</span>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={() => onEdit(display.raw || display)} className="btn-quiet is-block pressable">
          Edit
        </button>
        {confirmDelete ? (
          <>
            <button onClick={() => onDelete(display.raw || display)} className="btn-danger is-strong is-block pressable">
              Confirm delete
            </button>
            <button
              onClick={() => setConfirmDelete(false)}
              className="btn-quiet pressable"
              style={{ width: 44, height: 44, padding: 0, justifyContent: 'center', flexShrink: 0 }}
              aria-label="Cancel delete"
            >
              ✕
            </button>
          </>
        ) : (
          <button onClick={() => setConfirmDelete(true)} className="btn-danger is-block pressable">
            Delete
          </button>
        )}
      </div>
    </Modal>
  );
}

function IconColorPicker({ name, onSetAppearance }) {
  const current = getCategoryMeta(name);
  return (
    <div
      style={{
        padding: '12px 12px 14px',
        borderRadius: 8,
        background: 'var(--surface-2)',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <div>
        <div className="field-label">Icon</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(38px, 1fr))', gap: 6 }}>
          {CATEGORY_ICON_LIBRARY.map((ic) => {
            const active = current.icon === ic;
            return (
              <button
                key={ic}
                type="button"
                aria-label={`Use ${ic} icon`}
                onClick={() => onSetAppearance(name, { icon: ic, tint: current.tint })}
                className="pressable iconwrap"
                style={{
                  height: 38,
                  borderRadius: 8,
                  color: active ? '#fff' : current.tint,
                  background: active ? current.tint : `color-mix(in srgb, ${current.tint} 12%, transparent)`,
                  border: active ? '1px solid transparent' : '1px solid var(--line)',
                }}
              >
                <Icon name={ic} size={18} />
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <div className="field-label">Color</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {CATEGORY_TINTS.map((t) => {
            const active = current.tint?.toLowerCase() === t.toLowerCase();
            return (
              <button
                key={t}
                type="button"
                aria-label={`Use color ${t}`}
                onClick={() => onSetAppearance(name, { icon: current.icon, tint: t })}
                className="pressable"
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  background: t,
                  border: active ? '2px solid var(--ink)' : '2px solid transparent',
                  boxShadow: active ? '0 0 0 2px var(--surface-2)' : 'none',
                }}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function CategoryManager({ open, onClose, categories, categoryMeta, onAdd, onRemove, onRename, onSetAppearance }) {
  const [val, setVal] = useState('');
  const [editing, setEditing] = useState(null);
  const [editVal, setEditVal] = useState('');
  const [customizing, setCustomizing] = useState(null);
  // categoryMeta (the live overrides map) is read so this list re-renders
  // whenever a customization is saved; the values come from getCategoryMeta.
  void categoryMeta;
  const catList = buildCategoryList(categories);

  return (
    <Modal open={open} onClose={onClose} title="Categories" width={480}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <input
          value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder="New category"
          className="field"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && val.trim()) {
              onAdd(val.trim());
              setVal('');
            }
          }}
        />
        <button
          type="button"
          onClick={() => {
            if (val.trim()) {
              onAdd(val.trim());
              setVal('');
            }
          }}
          className="btn-primary pressable"
          aria-label="Add category"
          style={{ width: 40, height: 40, padding: 0, justifyContent: 'center', flexShrink: 0 }}
        >
          <Icon name="plus" size={18} sw={2.2} />
        </button>
      </div>
      {/* Hairline rows, like every other list in the app. Each category used
          to sit in its own filled pill, which turned a plain list into
          twenty stacked cards. */}
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {catList.map((c) => (
          <div key={c.name} style={{ display: 'flex', flexDirection: 'column' }}>
          <div className="row-line" style={{ alignItems: 'center', gap: 12 }}>
            <button
              type="button"
              aria-label={`Customize ${c.name} icon`}
              title="Change icon & color"
              onClick={() => setCustomizing(customizing === c.name ? null : c.name)}
              className="pressable iconwrap"
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                flexShrink: 0,
                color: c.tint,
                background: `color-mix(in srgb, ${c.tint} 14%, transparent)`,
                border: customizing === c.name ? `1px solid ${c.tint}` : '1px solid transparent',
              }}
            >
              <Icon name={c.icon} size={17} />
            </button>
            {editing === c.name ? (
              <input
                autoFocus
                value={editVal}
                onChange={(e) => setEditVal(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    onRename(c.name, editVal);
                    setEditing(null);
                  }
                  if (e.key === 'Escape') setEditing(null);
                }}
                className="field" style={{ flex: 1 }}
              />
            ) : (
              <span className="title" style={{ flex: 1, fontSize: 14.5, fontWeight: 500, color: 'var(--ink)' }}>{c.name}</span>
            )}
            {editing === c.name ? (
              <button
                type="button"
                onClick={() => {
                  onRename(c.name, editVal);
                  setEditing(null);
                }}
                className="pressable"
                style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink)' }}
              >
                Save
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setEditing(c.name);
                    setEditVal(c.name);
                  }}
                  className="pressable iconwrap"
                  style={{ width: 30, height: 30, borderRadius: 8, color: 'var(--ink-3)' }}
                >
                  <Icon name="edit" size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => onRemove(c.name)}
                  className="pressable iconwrap"
                  style={{ width: 30, height: 30, borderRadius: 8, color: 'var(--ink-3)' }}
                >
                  <Icon name="trash" size={16} />
                </button>
              </>
            )}
          </div>
          {customizing === c.name && (
            <IconColorPicker name={c.name} onSetAppearance={onSetAppearance} />
          )}
          </div>
        ))}
      </div>
    </Modal>
  );
}

export { Modal };
